import React from 'react';
import { Moment } from 'moment';
import {
	Flex,
	Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import {
	Currencies,
	DATE_AND_TIME,
	FuelTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatQuantity } from '@shared/utils/string';
import { Values } from '@shared/utils/objectEnums';
import type { Parcel } from '@api/features/voyages/bunker-stems/createBunkerStem';
import type { GetSuppliersResponse } from '@api/features/suppliers/getSuppliers';
import { formatDate } from '@client/utils/formatDate';
import { Links } from '@client/utils/links';
import { IncomingBunkerArray } from '../utils/bunkerTabHelpers';

const getParcelStr = (
	parcel: Parcel,
	currency: Values<typeof Currencies>,
) => {
	return `${formatQuantity(parcel.quantity, 'MT')} 
	${parcel.fuelGrade} @ ${formatCurrency(parcel.liftedPPT, currency)}/MT \n`;
};

export const getRobTableColumns = (showPricePerTon?: boolean) => {
	return (
		[
			{
				title: 'Fuel grade',
				dataIndex: 'fuelGrade',
				editable: true,
				editingProps: {
					type: 'select',
					inputProps: {
						defaultActiveFirstOption: true,
						options: Object.keys(FuelTypes).map((key) => ({
							label: FuelTypes[key as keyof typeof FuelTypes],
							value: key,
						})),
					},
				},
			},
			{
				title: 'Quantity',
				dataIndex: 'quantity',
				editable: true,
				align: 'right',
				editingProps: {
					type: 'number',
					inputProps: {
						addonAfter: 'MT',
					},
				},
				render: (q: number) => `${formatNumber(q, { separateThousands: true }, 3)} MT`,
			},
			...(showPricePerTon ? ([{
				dataIndex: 'pricePerTon',
				title: 'Price / MT',
				editable: true,
				editingProps: {
					type: 'number',
				},
				render: (c: number, record: IncomingBunkerArray[number]) => {
					const price = c;

					if (c == null || Number.isNaN(c)) {
						return 0;
					}

					return `${formatCurrency(price, record?.currency ?? 'USD')}`;
				},
			}]) : []),
		]
	);
};

export const getBunkerStemTableColumns = (
	suppliers: GetSuppliersResponse = [],
) => {
	return [
		{
			title: 'Stem date',
			dataIndex: 'stemDate',
			render: (val: Moment) => formatDate(val, DATE_AND_TIME, true, TimeZoneFormat.LocalTime),
		},
		{
			title: 'Invoice date',
			dataIndex: 'invoiceDate',
			render: (val: Moment) => formatDate(val) ?? (<em>-</em>),
		},
		{
			title: 'Parcels',
			dataIndex: 'parcels',
			render: (parcels: Parcel[]) => (
				<Flex vertical gap={5}>
					{parcels.map((p) => (
						<Typography.Text
							style={{ fontSize: 12 }}
						>
							{getParcelStr(p, p.currency)}
						</Typography.Text>
					))}
				</Flex>
			),
		},
		{
			title: 'Supplier',
			dataIndex: 'supplierId',
			render: (supplierId: number) => (
				<Typography.Text>
					{suppliers.find((s) => s.id === supplierId)?.name ?? 'N/A'}
				</Typography.Text>
			),
		},
		{
			title: 'Vendor expense',
			dataIndex: 'voyageExpenseId',
			render: (vendorExpenseId: number) => (
				<Link to={`${Links.Expenses.get()}?id=${vendorExpenseId}`}>
					<span>
						Go to vendor expense
					</span>
				</Link>
			),
		},
	];
};
